import { InjectionToken, inject } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { AiKnowledgeConfig, Namespace, TargetOption, targetIdentityKey } from '@galaxy/ai-knowledge';
import { ATLAS_CONFIG_TOKEN } from '@galaxy/atlas';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureFlagService } from '@galaxy/partner';
import { searchPartnerModels$ } from '@vendasta/protos/partner/v1/partner-model-service';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  AiAssistant,
  AiAssistantConfig,
  VENDASTA_AI_AVATAR_SVG_ICON,
  NamespaceConfig,
  SYSTEM_ASSISTANT_ID,
  PlatformLocation,
} from '@galaxy/ai-assistant';
import { AssistantType } from '@vendasta/protos/ai_assistants/v1alpha1';

const KNOWLEDGE_GRANTS_ENABLED_FLAG = 'knowledge_grants_enabled';

export const AI_KNOWLEDGE_CONFIG = new InjectionToken<AiKnowledgeConfig>(
  '[Vendasta Center Client]: Token for AI Knowledge config',
  {
    providedIn: 'root',
    factory: function (): AiKnowledgeConfig {
      const featureFlagService = inject(FeatureFlagService);
      const partnerId$ = inject(ATLAS_CONFIG_TOKEN).pipe(
        map((config) => config.partnerId),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      // Admin Center is the global surface: knowledge keys off the admin namespace regardless of the 'VA' partner id.
      const isAdminSurface$ = of(true);

      // Gate the new sharing view on the flag, read on the admin (VA) partner — same mechanism Partner Center uses.
      const knowledgeGrantsEnabled$ = partnerId$.pipe(
        switchMap((partnerId) =>
          featureFlagService.batchGetStatus(partnerId, '', [KNOWLEDGE_GRANTS_ENABLED_FLAG]),
        ),
        map((status) => Boolean(status?.[KNOWLEDGE_GRANTS_ENABLED_FLAG])),
        catchError(() => of(false)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      // Admins issue global grants: single partner, all-accounts-under-a-partner, all partners, all account groups.
      // canShareWithPartners drives the Partner / All-Partners affordances; account-group single targets are intentionally
      // off (DALE-1353 — the valid global options are partner-scoped, not single-account).
      const canShareWithPartners$ = of(true);
      const canShareWithAccountGroups$ = of(false);

      const partnerNameResolver = (ns: Namespace): Observable<string> => {
        if (ns.type === 'Global') return of('Vendasta');
        return of(ns.id);
      };

      // Partner picker, backing both the single-Partner and all-accounts-under-a-partner share targets.
      const partnersLookup = (query: string): Observable<TargetOption[]> =>
        searchPartnerModels$({
          pageSize: 25,
          filters: { searchTerm: (query ?? '').trim(), subscriptionTierId: '' },
        }).pipe(
          take(1),
          catchError(() => of({ partners: [] })),
          map((response) =>
            (response.partners ?? []).map((partner): TargetOption => ({
              identityKey: targetIdentityKey('Partner', partner.partnerId, 'self'),
              namespaceId: partner.partnerId,
              namespaceType: 'Partner',
              scope: 'self',
              displayName: partner.name || partner.partnerId,
              subtitle: partner.partnerId,
            })),
          ),
        );

      return {
        accountGroupId$: EMPTY,
        businessProfileUrl$: EMPTY,
        marketId$: EMPTY,
        partnerId$: partnerId$,
        showBusinessProfileSource$: EMPTY,
        manageKnowledgeUrl$: of('/ai-assistant/support/knowledge-base'),
        webChatWidgetEditRoute$: EMPTY,
        isAdminSurface$,
        knowledgeGrantsEnabled$,
        canShareWithPartners$,
        canShareWithAccountGroups$,
        partnerNameResolver,
        partnersLookup,
        // accountGroupsLookup / multiLocationGroupsLookup omitted — admins share at partner/global scope, not single accounts.
      };
    },
  },
);

export const AI_ASSISTANT_CONFIG = new InjectionToken<AiAssistantConfig>(
  '[Vendasta Center Client]: Token for AI Assistant config',
  {
    providedIn: 'root',
    factory: function (): AiAssistantConfig {
      const partnerId$ = inject(ATLAS_CONFIG_TOKEN).pipe(map((config) => config.partnerId));

      const namespaceConfig$: Observable<NamespaceConfig> = of({
        namespace: ({
          systemNamespace: {},
        }),
        root: `/ai-assistant`,
      }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

      return {
        accountGroupId$: of(''),
        marketId$: of(''),
        partnerId$: partnerId$,
        currentUserId$: of(''),
        defaultAIWorkforce$: of(AI_DEFAULT_VENDASTA_ASSISTANT_WORKFORCE),
        namespaceConfig$: namespaceConfig$,
        defaultConnections$: of([]),
        voiceAIAvailable$: of(false),
        showCustomAssistant$: of(true), // Admin center always shows custom assistants
        platformLocation: PlatformLocation.PLATFORM_LOCATION_ADMIN_CENTER,
        aiMarketerAvailable$: of(false),
      };
    },
  },
);

export const AI_DEFAULT_VENDASTA_ASSISTANT_WORKFORCE: AiAssistant[] = [
  {
    assistant: {
      id: SYSTEM_ASSISTANT_ID,
      name: 'AI_ASSISTANT.SETTINGS.SYSTEM_ASSISTANT.DESCRIPTION',
      type: AssistantType.ASSISTANT_TYPE_SYSTEM,
    },
    descriptionKey: 'AI_ASSISTANT.SETTINGS.SYSTEM_ASSISTANT.DESCRIPTION',
    decoration: {
      defaultAvatarIcon: VENDASTA_AI_AVATAR_SVG_ICON,
      gradientColor: '#22C0CA',
    },
    isDefault: true,
    showConfigButtonOnDefault: true,
    alertInformation: 'AI_ASSISTANT.SETTINGS.SYSTEM_ASSISTANT.ALERT_INFORMATION',
    hideConnections: true,
  },
];
